import { difference, intersection, shuffle } from "es-toolkit";

type StudentId = string;
export type GroupRestriction = StudentId[];
type Group = StudentId[];
interface GroupMapping {
  // maps studentId to group number
  [k: string]: number;
}

const MAX_ITERATIONS = 10;

function bypassesRestrictions(groupMapping: GroupMapping, restrictions: GroupRestriction[]): boolean {
  return restrictions.every((restriction: string[]) => {
    const assignedGroups = restriction.map((studentId: string) => groupMapping[studentId]);
    // if every student in the restriction is assigned to a different group, then we're good to go
    return new Set(assignedGroups).size === restriction.length;
  });
}

export default function createGroups(
  studentIds: StudentId[],
  size: number,
  restrictions: GroupRestriction[] = [],
): Group[] {
  const numGroups = Math.ceil(studentIds.length / size);
  const groups = Array.from({ length: numGroups }, (): string[] => []);

  // not all studentIds in the restrictions list could be present today, so filter out restrictions that don't apply
  restrictions = restrictions
    .map((restriction) => intersection(restriction, studentIds))
    .filter((restriction) => restriction.length > 1); // restrictions of only one student don't mean anything

  // get unique ids from restrictions
  const studentsWithRestrictions = [...new Set(restrictions.flat())];

  // assign the students with restrictions to groups first
  const groupMapping: GroupMapping = {};
  let numIterations = 0;
  let shuffledStudents: StudentId[] = studentsWithRestrictions;
  do {
    // shuffle them and assign them to groups
    shuffledStudents = shuffle(shuffledStudents);
    let index = 0;
    for (const id of shuffledStudents) {
      groupMapping[id] = index % numGroups;
      index++;
    }

    if (++numIterations >= MAX_ITERATIONS) {
      throw new Error("Unable to create groups with the current set of restrictions");
    }
    // repeat if the current grouping is illegal
  } while (!bypassesRestrictions(groupMapping, restrictions));

  // now that groups for restricted students are determined put them into arrays
  shuffledStudents.forEach((id, i) => {
    groups[i % numGroups].push(id);
  });

  // shuffle remaining students
  let remainingStudents = shuffle(difference(studentIds, studentsWithRestrictions));
  // and add them to groups
  groups.forEach((group: string[], i: number) => {
    const numStudentsNeeded = size - group.length;
    groups[i] = group.concat(remainingStudents.slice(0, numStudentsNeeded));
    remainingStudents = remainingStudents.slice(numStudentsNeeded);
  });

  return groups;
}
